import { useRef, useState } from 'react';
import { Alert, Divider, Drawer, Tooltip } from 'antd';
import ChevronLeftIcon from 'components/icons/ChevronLeft';
import Button from 'components/new-ui/base/button/Button';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import Notification from 'components/ui/base/notification/Notification';
import { ICS2InformationExchangeMessages } from 'store/declarations/eu/ics2/ICS2InformationExchangeMessages';
import { Ics2CustomsRequest, Ics2Referral, Ics2ReferralDraft } from 'store/declarations/eu/ics2/customs-requests/types';
import { isAmendmentReferral, isReferralEditable } from 'store/declarations/eu/ics2/customs-requests/utils';
import { sleep } from 'utils/common';
import MoreInformation from 'views/declarations/common/ics2-message-details/MoreInformation';
import IE3Q02ResponseForm from './IE3Q02/ResponseForm';
import IE3Q03ResponseForm from './IE3Q03/ResponseForm';
import ReferralSummary from './ReferralSummary';
import { getRequestTitle } from './referralLabels';
import { Ics2SendOutcome } from './responseForm';
import { ResponseFormHandle } from '../responseForm';
import useIcs2ReferralAttachments from './hooks/useIcs2ReferralAttachments';
import { DrawerActions, DrawerTitle } from '../styles';

type Props = {
    declarationId?: string;
    request: Ics2CustomsRequest;
    referral: Ics2Referral;
    onSave: (request: Ics2CustomsRequest, referral: Ics2Referral, draft: Ics2ReferralDraft) => Promise<unknown>;
    onSaveAndSend: (
        request: Ics2CustomsRequest,
        referral: Ics2Referral,
        draft: Ics2ReferralDraft
    ) => Promise<Ics2SendOutcome>;
    dispatchError?: string;
    onClose: () => void;
};

const MIN_BUSY_MS = 1000;

const ReferralDrawer = ({ declarationId, request, referral, onSave, onSaveAndSend, dispatchError, onClose }: Props) => {
    const formRef = useRef<ResponseFormHandle>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendFailed, setSendFailed] = useState(false);

    const isScreening = request.messageType === ICS2InformationExchangeMessages.IE3Q03;
    const editable = isReferralEditable(referral.status);
    const isAmendment = isAmendmentReferral(referral.requestType);

    const attachments = useIcs2ReferralAttachments({
        declarationId,
        notificationId: request.notificationId,
        referralRequestReference: referral.referralRequestReference,
        isScreening,
    });

    const [blockingAttachment] = attachments.attachmentErrors;

    /**
     * Holds the spinner for `MIN_BUSY_MS` even when the request beats it. A save often returns
     * inside a single frame, and a button that flickers through its loading state reads as a glitch
     * rather than as the answer having been written down.
     */
    const withBusy =
        <T,>(setBusy: (busy: boolean) => void, run: (draft: Ics2ReferralDraft) => Promise<T>) =>
        async (draft: Ics2ReferralDraft) => {
            setBusy(true);
            const startedAt = Date.now();
            try {
                return await run(draft);
            } finally {
                await sleep(Math.max(0, MIN_BUSY_MS - (Date.now() - startedAt)));
                setBusy(false);
            }
        };

    const handleSave = withBusy(setIsSaving, async (draft: Ics2ReferralDraft) => {
        const saved = await onSave(request, referral, draft);
        if (saved) {
            Notification({
                type: 'success',
                messageTitle: 'Draft saved',
                description: `Your answer to ${referral.referralRequestReference} has been saved.`,
                placement: 'top',
            });
        }
        return saved;
    });

    const handleSaveAndSend = withBusy(setIsSending, async (draft: Ics2ReferralDraft) => {
        setSendFailed(false);
        const outcome = await onSaveAndSend(request, referral, draft);
        setSendFailed(outcome === 'failed');
        return outcome;
    });

    const sendIfComplete = async () => {
        const blocking = await formRef.current?.submit();
        if (!blocking?.length) return;

        Notification({
            type: 'error',
            messageTitle: `${referral.referralRequestReference} is not ready to send`,
            description: blocking.join(' '),
            placement: 'top',
        });
    };

    const ResponseForm = isScreening ? IE3Q03ResponseForm : IE3Q02ResponseForm;

    const sendButton = (
        <Button
            type="primary"
            loading={isSending}
            disabled={isSaving || Boolean(blockingAttachment)}
            onClick={sendIfComplete}
        >
            Send
        </Button>
    );

    return (
        <Drawer
            open
            placement="right"
            width={600}
            closable={false}
            onClose={onClose}
            title={
                <DrawerTitle>
                    <IconButton icon={<ChevronLeftIcon />} tooltipText="Back" onClick={onClose} />
                    {getRequestTitle(request.messageType)}
                </DrawerTitle>
            }
            extra={
                editable && (
                    <DrawerActions>
                        <Button
                            loading={isSaving}
                            disabled={isSending}
                            onClick={async () => {
                                await formRef.current?.save();
                                onClose();
                            }}
                        >
                            Save as Draft
                        </Button>
                        {blockingAttachment ? (
                            <Tooltip title={blockingAttachment}>
                                <span>{sendButton}</span>
                            </Tooltip>
                        ) : (
                            sendButton
                        )}
                    </DrawerActions>
                )
            }
        >
            <ReferralSummary request={request} referral={referral} />

            {isAmendment && (
                <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    message="Customs asked for the ENS filing to be amended"
                    description="Lodge an amendment quoting this referral request reference. If the amendment cannot be made, answer here with code R203 explaining why; once it has been accepted, answer with code R103 and point at it."
                />
            )}

            <Divider />

            {sendFailed && !dispatchError && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="error"
                    showIcon
                    message="This response was not sent"
                    description="Your answer is saved but has not gone to Customs. Review it and try again - if it keeps failing, contact our support team, quoting this referral request reference."
                />
            )}

            {dispatchError ? (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="error"
                    showIcon
                    message="This response did not reach customs system"
                    description={
                        // A rejected message comes back editable, so the answer can be corrected and
                        // sent again. A blocked one does not: the dispatcher hit a permanent gateway
                        // failure and holds the snapshot until someone looks at it - there is nothing
                        // the user can do from here, so point them at someone who can.
                        editable
                            ? 'Correct the answer below and send it again. If it keeps failing, contact our support team.'
                            : 'Please contact our support team, quoting this referral request reference.'
                    }
                />
            ) : (
                !editable && (
                    <Alert
                        style={{ marginBottom: 16 }}
                        type="info"
                        showIcon
                        message="This referral can no longer be edited"
                        description="The answer below is in flight or already sent. Customs does not confirm receipt of a referral response."
                    />
                )
            )}

            <ResponseForm
                ref={formRef}
                declarationId={declarationId}
                notificationId={request.notificationId}
                referral={referral}
                attachments={attachments}
                disabled={!editable}
                onSave={handleSave}
                onSubmit={handleSaveAndSend}
            />

            <Divider />

            <MoreInformation />
        </Drawer>
    );
};

export default ReferralDrawer;
